import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProgress } from '../contexts/ProgressContext';
import { sections, lessons, getLessonsBySection, courseInfo } from '../data/courseData';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
    Eye, 
    LogOut, 
    Star,
    ExternalLink,
    ChevronRight,
    Sparkles,
    Shield,
    User
} from 'lucide-react';

const HERO_BG = "https://customer-assets.emergentagent.com/job_gamma-to-web/artifacts/brgxn5xe_Grok%20Image%202026-02-05%20at%2017.41.51.png";

const colorClasses = {
    primary: {
        border: 'border-primary/30',
        text: 'text-primary',
        glow: 'hover:shadow-gold'
    },
    mystical: {
        border: 'border-mystical/30',
        text: 'text-mystical-glow',
        glow: 'hover:shadow-mystical'
    },
    accent: {
        border: 'border-accent/30',
        text: 'text-accent-glow',
        glow: 'hover:shadow-[0_4px_24px_-4px_hsl(350_60%_45%/0.4)]'
    },
    emerald: {
        border: 'border-emerald/30',
        text: 'text-emerald',
        glow: 'hover:shadow-[0_4px_24px_-4px_hsl(160_45%_35%/0.4)]'
    }
};

const DashboardPage = () => {
    const { user, logout, isAdmin } = useAuth();
    const { getSectionProgress, getOverallProgress, syncing } = useProgress();
    const navigate = useNavigate();

    const overallProgress = getOverallProgress(lessons);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const handleSectionClick = (sectionId) => {
        navigate(`/section/${sectionId}`);
    };

    return (
        <div className="min-h-screen relative">
            {/* Background image */}
            <div 
                className="fixed inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${HERO_BG})` }}
            />
            <div className="fixed inset-0 bg-background/80" />
            {/* Header */}
            <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50 relative">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-card border border-primary/30 shadow-gold">
                                <Eye className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <h1 className="font-display text-lg font-semibold text-foreground">
                                    {courseInfo.title}
                                </h1>
                                <div className="flex items-center gap-2">
                                    {user?.picture ? (
                                        <img 
                                            src={user.picture} 
                                            alt={user.name}
                                            className="w-5 h-5 rounded-full object-cover"
                                        />
                                    ) : (
                                        <User className="w-4 h-4 text-muted-foreground" />
                                    )}
                                    <p className="text-sm text-muted-foreground font-ui">
                                        {user?.name || 'Seeker'}
                                        {isAdmin && (
                                            <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-primary/20 text-primary">
                                                Admin
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4">
                            {syncing && (
                                <span className="text-xs text-muted-foreground font-ui animate-pulse">
                                    Syncing...
                                </span>
                            )}
                            {isAdmin && (
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => navigate('/admin')}
                                    data-testid="admin-link"
                                >
                                    <Shield className="w-4 h-4" />
                                    <span className="hidden sm:inline">Admin</span>
                                </Button>
                            )}
                            {courseInfo.appLink && courseInfo.appLink !== '#' && (
                                <Button variant="codex" size="sm" asChild>
                                    <a href={courseInfo.appLink} target="_blank" rel="noopener noreferrer">
                                        <ExternalLink className="w-4 h-4" />
                                        <span className="hidden sm:inline">Open App</span>
                                    </a>
                                </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="dashboard-logout-btn">
                                <LogOut className="w-4 h-4" />
                                <span className="hidden sm:inline">Logout</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 relative z-10">
                {/* Hero Section */}
                <div className="text-center mb-12 fade-in">
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                        {courseInfo.title}
                    </h2>
                    <p className="font-body text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                        {courseInfo.description}
                    </p>

                    {/* Overall Progress */}
                    <div className="max-w-md mx-auto">
                        <div className="flex items-center justify-between mb-2">
                            <span className="font-ui text-sm text-muted-foreground">
                                Overall Progress
                            </span>
                            <span className="font-ui text-sm font-medium text-primary">
                                {overallProgress.completed} / {overallProgress.total} Lessons
                            </span>
                        </div>
                        <Progress 
                            value={overallProgress.percentage} 
                            className="h-2 bg-muted"
                        />
                        <p className="mt-2 font-ui text-sm text-muted-foreground">
                            {overallProgress.percentage}% Complete
                        </p>
                    </div>
                </div>

                {/* Sections Grid */}
                <div className="grid gap-6 md:grid-cols-2">
                    {sections.map((section, index) => {
                        const colors = colorClasses[section.color] || colorClasses.primary;
                        const sectionLessons = getLessonsBySection(section.id);
                        const progress = getSectionProgress(section.id, lessons);

                        return (
                            <Card
                                key={section.id}
                                className={`group cursor-pointer card-mystical ${colors.glow} fade-in`}
                                style={{ animationDelay: `${index * 0.1}s` }}
                                onClick={() => handleSectionClick(section.id)}
                            >
                                <CardHeader className="pb-3">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className={`font-display text-sm ${colors.text} mb-1`}>
                                                {section.number}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {progress.completed > 0 && (
                                                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 border border-primary/30">
                                                    <Star className="w-3 h-3 text-primary fill-primary" />
                                                    <span className="text-xs font-ui text-primary">
                                                        {progress.completed}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <CardTitle className="text-xl group-hover:text-primary transition-colors">
                                            {section.title}
                                        </CardTitle>
                                        <CardDescription className="font-body text-base italic mt-1">
                                            {section.subtitle}
                                        </CardDescription>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="font-body text-sm text-muted-foreground mb-4 line-clamp-3">
                                        {section.description}
                                    </p>
                                    
                                    {/* Section Progress */}
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="font-ui text-xs text-muted-foreground">
                                                {sectionLessons.length} Lessons
                                            </span>
                                            <span className="font-ui text-xs text-muted-foreground">
                                                {progress.percentage}%
                                            </span>
                                        </div>
                                        <Progress 
                                            value={progress.percentage} 
                                            className="h-1.5 bg-muted"
                                        />
                                    </div>

                                    {/* Enter Button */}
                                    <div className="mt-4 flex justify-end">
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            className="group-hover:text-primary group-hover:bg-primary/10"
                                        >
                                            Enter Section
                                            <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* App Link Banner */}
                {courseInfo.appLink && courseInfo.appLink !== '#' && (
                    <div className="mt-12 fade-in" style={{ animationDelay: '0.4s' }}>
                        <Card className="bg-gradient-to-r from-card via-card-elevated to-card border-primary/20">
                            <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 rounded-lg bg-primary/10 border border-primary/30">
                                        <Sparkles className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-display text-lg font-semibold text-foreground">
                                            Decrypted English App
                                        </h3>
                                        <p className="font-body text-sm text-muted-foreground">
                                            Practice your decryption skills with our interactive app
                                        </p>
                                    </div>
                                </div>
                                <Button variant="gold" asChild>
                                    <a href={courseInfo.appLink} target="_blank" rel="noopener noreferrer">
                                        <ExternalLink className="w-4 h-4" />
                                        Open App
                                    </a>
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                )}
            </main>

            {/* Footer */}
            <footer className="border-t border-border/50 bg-card/30 backdrop-blur-sm mt-16 relative z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
                    <p className="font-ui text-sm text-muted-foreground">
                        &copy; {new Date().getFullYear()} Gringos English. All rights reserved.
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default DashboardPage;
